import clsx from 'clsx';
import { bool, func, number, object } from 'prop-types';
import { createPortal } from 'react-dom';

import { FileModal, Icon, LeavingConfirmationModal } from 'components';
import { useSafeState } from 'hooks';

/**
 * Component to display a CTA button from Strapi
 * action type available: 'cta.phone-cta' | 'cta.modal-cta' | 'cta.file-cta'
 * @props {cta} cta object from Strapi
 * @props {modalContent} function returning the modal content from the parent component
 * @props {index} index used to open the right modal if MultiActionButton is used in a loop
 * @props {phoneIconButton} boolean to display a phone icon button on mobile only
 * @props {drawer} boolean to display the action button in a drawer on mobile only
 */
const MultiActionButton = ({
  cta,
  modalContent,
  index = 0,
  phoneIconButton = false,
  drawer = false,
  onCloseDrawer = null
}) => {
  const [modalIndexOpen, setModalIndexOpen] = useSafeState(null);
  const [showPhoneNumber, setShowPhoneNumber] = useSafeState(false);
  const [pdfIndexOpen, setPdfIndexOpen] = useSafeState(null);

  const triggerAction = (type) => {
    onCloseDrawer && onCloseDrawer();

    switch (type) {
      case 'modal':
        setModalIndexOpen(index);
        break;
      case 'file':
        setPdfIndexOpen(index);
        break;
      default:
        return;
    }
  };

  const renderCta = () => {
    switch (cta.component) {
      case 'cta.phone-cta':
        return (
          <>
            {!showPhoneNumber && !drawer && (
              <>
                {phoneIconButton ? (
                  <a href={`tel:${cta.phoneNumber}`} className="btn small bg-yellow-500 lg:hidden">
                    <Icon name="phone" classes="w-6 h-6 text-white" />
                  </a>
                ) : (
                  <a
                    className={clsx('btn w-fit small', index !== 0 && 'outlined')}
                    onMouseEnter={() => setShowPhoneNumber(true)}
                    href={`tel:${cta.phoneNumber}`}
                  >
                    {cta.buttonText}
                  </a>
                )}
              </>
            )}

            {showPhoneNumber && !drawer && (
              <div
                onMouseLeave={() => setShowPhoneNumber(false)}
                className={clsx('btn w-fit small', index !== 0 && 'outlined')}
              >
                {cta.phoneNumber}
              </div>
            )}

            {drawer && (
              <a className="flex items-center gap-6" href={`tel:${cta.phoneNumber}`}>
                <div className="bg-primary-500/10 text-primary-500 rounded-full w-10 h-10 flex-center-center">
                  <Icon name="phone" />
                </div>

                <div>
                  {cta.buttonText}
                  <span className="block">{cta.phoneNumber}</span>
                </div>
              </a>
            )}
          </>
        );
      case 'cta.modal-cta':
        return (
          <>
            <button
              type="button"
              className={clsx(drawer ? 'flex items-center gap-6' : 'btn w-fit small', index !== 0 && 'outlined')}
              onClick={() => triggerAction('modal')}
            >
              {drawer && (
                <div className="bg-primary-500/10 text-primary-500 rounded-full w-10 h-10 flex-center-center">
                  <Icon name="note-sticky" iconStyle="fad" />
                </div>
              )}
              {cta.buttonText}
            </button>

            {createPortal(
              <LeavingConfirmationModal
                isOpen={modalIndexOpen === index}
                modal={modalContent(index)}
                onClose={() => setModalIndexOpen(null)}
              />,
              document.getElementById('react-modal-portal')
            )}
          </>
        );
      case 'cta.file-cta':
        return (
          <>
            <button
              type="button"
              className={clsx(drawer ? 'flex items-center gap-6' : 'btn w-fit small', index !== 0 && 'outlined')}
              onClick={() => triggerAction('file')}
            >
              {drawer && (
                <div className="bg-primary-500/10 text-primary-500 rounded-full w-10 h-10 flex-center-center">
                  <Icon name="file-magnifying-glass" iconStyle="fad" />
                </div>
              )}
              {cta.buttonText}
            </button>

            {createPortal(
              <FileModal
                showPDF={pdfIndexOpen === index}
                onClose={() => setPdfIndexOpen(null)}
                modal={modalContent(index)}
              />,
              document.getElementById('react-modal-portal')
            )}
          </>
        );
      case 'cta.link-cta':
        return (
          <a
            className={clsx(drawer ? 'flex items-center gap-6' : 'btn w-fit small', index !== 0 && 'outlined')}
            href={cta.link}
          >
            {drawer && (
              <div className="bg-primary-500/10 text-primary-500 rounded-full w-10 h-10 flex-center-center">
                <Icon name="external-link" iconStyle="fad" />
              </div>
            )}
            {cta.buttonText}
          </a>
        );
      default:
        return null;
    }
  };

  return renderCta();
};

MultiActionButton.propTypes = {
  cta: object.isRequired,
  modalContent: func.isRequired,
  index: number,
  phoneIconButton: bool,
  drawer: bool,
  onCloseDrawer: func
};

export default MultiActionButton;
